import {
  MdOutlineAccountCircle,
  MdOutlineSettings,
  MdOutlineNotifications,
  MdOutlineDarkMode,
  MdOutlineHelpOutline,
  MdOutlineLogout,
} from "react-icons/md";
import TotalRequestIcon, {
  BuyIcon,
  ExchangeIcon,
  LiveBotIcon,
  ProductManagmentIcon,
  SampleApprovalIcon,
  SellIcon,
  StockSummaryIcon,
  TrackRecordIcon,
  UserInfoIcon,
} from "./svgs";
import { NavItem, ProductRequest, TrackRecordItem } from "./types";
import { StockRequestApiItem, StockSummaryCardApi } from "./services/stockSummaryService";
import { formatCurrency } from "./utils/display";
import { formatDate, formatDateIfLikely } from "./utils/date";

export const MAIN_NAV: NavItem[] = [
  { label: "Stock Summary", href: "/", icon: <StockSummaryIcon /> },
  { label: "Track Record", href: "/track-record", icon: <TrackRecordIcon /> },
  {
    label: "Sample Approval",
    href: "/sample-approval",
    icon: <SampleApprovalIcon />,
  },
  {
    label: "Product Management",
    href: "/product-management",
    icon: <ProductManagmentIcon />,
  },
];

export const SYSTEM_NAV: NavItem[] = [
  {
    label: "User Information",
    href: "/user-information",
    icon: <UserInfoIcon />,
  },
  { label: "Live Bot", href: "/live-bot", icon: <LiveBotIcon /> },
];

export const USER_MENU_ITEMS = [
  {
    icon: <MdOutlineAccountCircle size={16} />,
    label: "View Profile",
    danger: false,
  },
  {
    icon: <MdOutlineSettings size={16} />,
    label: "Account Settings",
    danger: false,
  },
  {
    icon: <MdOutlineNotifications size={16} />,
    label: "Notifications",
    danger: false,
  },
  { icon: <MdOutlineDarkMode size={16} />, label: "Dark Mode", danger: false },
  {
    icon: <MdOutlineHelpOutline size={16} />,
    label: "Help & Support",
    danger: false,
  },
  { icon: <MdOutlineLogout size={16} />, label: "Sign Out", danger: true },
] as const;

export const EXCLUDED_DETAIL_KEYS = new Set([
  'sampleId',
  'id',
  '_id',
  'product',
  'productName',
  'priceUnit',
  'price_unit',
]);

export const getDetailEntries = (raw: Record<string, unknown>) =>
  Object.entries(raw)
    .filter(
      ([key, value]) =>
        !EXCLUDED_DETAIL_KEYS.has(key) &&
        value !== null &&
        value !== undefined &&
        value !== '' &&
        typeof value !== 'object'
    )
    .map(([key, value]) => ({
      label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
      value: String(value),
    }));

export const DEFAULT_PAGE = 1;
export const DEFAULT_LIMIT = 10;

export const getCardConfig = (card: StockSummaryCardApi) => {
  switch (card.key) {
    case 'buy':
      return {
        badge: 'BUY',
        badgeColor: 'pink' as const,
        icon: <BuyIcon className="h-5 w-5" />,
      };
    case 'sell':
      return {
        badge: 'SELL',
        badgeColor: 'yellow' as const,
        icon: <SellIcon className="h-5 w-5" />,
      };
    case 'exchange':
      return {
        badge: 'Exchange',
        badgeColor: 'blue' as const,
        icon: <ExchangeIcon className="h-5 w-5" />,
      };
    case 'active':
      return {
        badge: 'Active',
        badgeColor: 'blue' as const,
        icon: <TotalRequestIcon className="h-5 w-5" />,
      };
    case 'total':
    default:
      return {
        badge: 'Total',
        badgeColor: 'gray' as const,
        icon: <TotalRequestIcon className="h-5 w-5" />,
      };
  }
};

export const getDetailedStockEntries = (request: StockRequestApiItem) => [
  { label: 'User Name', value: request.userName },
  { label: 'Phone Number', value: request.phoneNumber },
  { label: 'Address', value: request.address },
  { label: 'Operation Type', value: request.operationType },
  { label: 'Product Name', value: request.productName },
  { label: 'Quantity', value: request.quantity },
  { label: 'Amount', value: request.amount ? formatCurrency(request.amount) : request.amount },
  { label: 'Pin Code', value: request.pincode },
  { label: 'Status', value: request.status },
  { label: 'Bank Details or UPI', value: request.bankDetails },
  { label: 'Created At', value: request.createdAt },
];

export const getDetailedEntries = (record: TrackRecordItem) => [
  { label: 'User Name', value: record.userName },
  { label: 'Phone Number', value: record.phoneNumber },
  { label: 'Address', value: record.address },
  { label: 'Operation Type', value: record.operationType },
  { label: 'Product Name', value: record.productName },
  { label: 'Quantity', value: record.quantity },
  { label: 'Amount', value: formatCurrency(record.amount) },
  { label: 'Pin Code', value: record.pincode },
  { label: 'Status', value: record.statusLabel || record.status },
  { label: 'Bank Details', value: record.bankDetails },
  { label: 'Created At', value: record.createdAt },
  { label: 'Updated At', value: record.updatedAt },
];

export const formatDetailValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }

  if (typeof value === 'string') {
    return formatDateIfLikely(value);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

export const getDisplayStatus = (
  status: string | null | undefined,
  statusLabel: string | null | undefined
): ProductRequest['status'] => {
  const normalizedStatus = (status || statusLabel || '').trim().toUpperCase();

  switch (normalizedStatus) {
    case 'COMPLETE':
    case 'COMPLETED':
      return 'Completed';
    case 'PROCESS':
    case 'PROCESSING':
      return 'Processing';
    case 'CANCEL':
    case 'CANCELLED':
      return 'Cancelled';
    case 'DISPATCH':
    case 'DISPATCHED':
      return 'Dispatch';
    default:
      return 'Pending';
  }
};

export const toTitleCase = (value: string | null | undefined) => {
  if (!value) return '--';

  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const formattedCurrency = (value: string | null) => {
  if (!value) return '--';

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return value;
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export interface UserData {
  id: string;
  userName: string;
  contactNumber: string;
  whatsappNumber: string;
  location: string;
  isBlacklisted: boolean;
  address: string;
  pincode: string;
  isPremium: boolean;
  createdAt: string;
  updatedAt: string;
}

export const mapApiUserToUserData = (user: {
  id: string;
  fullName: string;
  contactNumber: string;
  whatsappNumber?: string;
  city: string;
  isBlacklisted: boolean;
  address: string;
  pincode: string;
  isPremium: boolean;
  createdAt: string;
  updatedAt: string;
}): UserData => ({
  id: user.id,
  userName: user.fullName,
  contactNumber: user.contactNumber,
  whatsappNumber: user.whatsappNumber || '',
  location: user.city,
  isBlacklisted: user.isBlacklisted,
  address: user.address,
  pincode: user.pincode,
  isPremium: user.isPremium,
  createdAt: user.createdAt,
  updatedAt: user.updatedAt,
});

export const getDetailUserEntries = (user: UserData) => [
  { label: 'Full Name', value: user.userName },
  { label: 'Phone Number', value: user.contactNumber },
  { label: 'WhatsApp Number', value: user.whatsappNumber },
  { label: 'Location/City', value: user.location },
  { label: 'Address', value: user.address },
  { label: 'Pincode', value: user.pincode },
  { label: 'Premium User', value: user.isPremium ? 'Yes' : 'No' },
  { label: 'Created At', value: formatDate(user.createdAt) },
  { label: 'Updated At', value: formatDate(user.updatedAt) },
];

export function createExcelWorkbookXml(rows: UserData[]) {
  const escapeXml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

  const headers = [
    'User Name',
    'Contact Number',
    'WhatsApp Number',
    'Location/City',
    'Blacklisted',
    'Address',
    'Pincode',
    'Premium User',
    'Created At',
    'Updated At',
  ];

  const dataRows = rows.map((user) => [
    user.userName || '--',
    user.contactNumber || '--',
    user.whatsappNumber || '--',
    user.location || '--',
    user.isBlacklisted ? 'Yes' : 'No',
    user.address || '--',
    user.pincode || '--',
    user.isPremium ? 'Yes' : 'No',
    formatDate(user.createdAt),
    formatDate(user.updatedAt),
  ]);

  const buildCells = (values: string[]) =>
    values.map((value) => `<Cell><Data ss:Type="String">${escapeXml(value)}</Data></Cell>`).join('');

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Users">
  <Table>
   <Row>${buildCells(headers)}</Row>
   ${dataRows.map((row) => `<Row>${buildCells(row)}</Row>`).join('')}
  </Table>
 </Worksheet>
</Workbook>`;
}